import {
    CYCLE_TIMELINE_LAYOUT,
    Colors,
    Icon,
    Text,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';

import type { ColorType } from '@amelio/ui-library';
import type { ReactNode } from 'react';

export interface FormFieldProps {
    label: string;

    children: ReactNode;

    errorMessage?: string;

    required?: boolean;

    disabled?: boolean;

    className?: string;

    id?: string;

    dataTestId?: string;

    layout?: 'vertical' | 'horizontal';

    helperText?: string;

    spacing?: 'tight' | 'snug' | 'normal' | 'loose';

    labelSize?: 'small' | 'default' | 'large';

    labelTransform?: 'none' | 'uppercase';

    labelWeight?: 'medium' | 'bold';

    labelClassName?: string;

    labelColor?: ColorType;
}

export const FormField = ({
    label,
    children,
    errorMessage,
    required = false,
    disabled = false,
    className = '',
    id,
    dataTestId,
    layout = 'vertical',
    helperText,
    spacing = 'normal',
    labelSize = 'default',
    labelTransform = 'none',
    labelWeight = 'medium',
    labelClassName,
    labelColor = Colors.BASE,
}: FormFieldProps) => {
    const hasError = Boolean(errorMessage);
    const hasHelper = Boolean(helperText);

    const isHorizontal = layout === 'horizontal';

    const spacingClasses = {
        tight: 'gap-0.5',
        snug: CYCLE_TIMELINE_LAYOUT.fieldGap,
        normal: 'gap-2',
        loose: 'gap-4',
    }[spacing];

    const layoutClasses = isHorizontal ? 'flex-row items-center' : 'flex-col';
    const containerClasses = `flex ${layoutClasses} ${spacingClasses} ${className}`;

    const labelWrapperClasses = isHorizontal ? 'flex items-center gap-1 flex-shrink-0' : '';

    return (
        <div
            className={containerClasses}
            data-testid={dataTestId}
        >
            <div className={labelWrapperClasses || 'flex items-center gap-1'}>
                <Text
                    as={'label'}
                    htmlFor={id}
                    weight={labelWeight}
                    size={labelSize}
                    transform={labelTransform}
                    className={labelClassName}
                    color={disabled ? Colors.BASE_300 : labelColor}
                    data-testid={dataTestId ? `${dataTestId}-label` : undefined}
                >
                    {label}
                </Text>

                {required && (
                    <span
                        className={'ml-0.5'}
                        style={{ color: Colors.ERROR }}
                        aria-label={'required'}
                    >
                        *
                    </span>
                )}

                {hasHelper && (
                    <TooltipProvider delayDuration={200}>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <span className={'inline-flex'}>
                                    <Icon
                                        name={'essential-info-circle'}
                                        size={'sm'}
                                        color={Colors.BASE_400}
                                        className={'cursor-help'}
                                        ariaLabel={`Help for ${label}`}
                                    />
                                </span>
                            </TooltipTrigger>

                            <TooltipContent side={'top'}>
                                <p>{helperText}</p>
                            </TooltipContent>
                        </Tooltip>
                    </TooltipProvider>
                )}
            </div>

            <div className={`flex flex-col ${isHorizontal ? 'min-w-0 flex-1' : ''}`}>
                {children}

                {hasError && (
                    <div
                        className={'flex items-center gap-1'}
                        role={'alert'}
                        aria-live={'polite'}
                        data-testid={dataTestId ? `${dataTestId}-error` : undefined}
                    >
                        <Icon
                            name={'essential-danger'}
                            size={'sm'}
                            color={Colors.ERROR}
                        />

                        <Text
                            size={'small'}
                            color={Colors.ERROR}
                        >
                            {errorMessage}
                        </Text>
                    </div>
                )}
            </div>
        </div>
    );
};
